import dayjs from 'dayjs'
import { formatBoxOfficeAmount, formatPercent } from './dashboard-market-rank.js'

/** 「指定なし」を表すセンチネル。バックエンドの DpCinemaRankQuery.VERSION_NONE と一致させること */
export const VERSION_NONE = 'NONE'

/** 上映バージョンの4次元。key はレスポンス／リクエストのフィールド名に対応 */
export const VERSION_DIMENSIONS = [
  { key: 'versionIds', optionsKey: 'versionList', label: '2D/3D' },
  { key: 'version1Ids', optionsKey: 'version1List', label: 'スクリーン' },
  { key: 'version2Ids', optionsKey: 'version2List', label: '字幕/吹替' },
  { key: 'version3Ids', optionsKey: 'version3List', label: 'その他' },
]

/** ソート方式。code はバックエンド DpCinemaRankSort の code と一致させること */
export const SORT_OPTIONS = [
  { code: 'boxOffice', label: '興行収入' },
  { code: 'scheduleCount', label: '上映回数' },
  { code: 'seatTotal', label: '座席数' },
  { code: 'occupiedSeats', label: '入場者数' },
  { code: 'occupancyRate', label: '充足率' },
]

/** 並び替え方向。バックエンド DpSortOrder の code と一致させること */
export const SORT_ORDER_ASC = 'asc'
export const SORT_ORDER_DESC = 'desc'

/** 時間帯の表示順（バックエンドの MetricGroup のフィールド名と対応） */
export const TIME_SLOTS = [
  { key: 'morning', label: '朝' },
  { key: 'afternoon', label: '昼' },
  { key: 'night', label: '夜' },
]

/** 整数を桁区切りで。null/未定義は「-」 */
export function formatCount(value) {
  if (value == null || value === '') return '-'
  const n = Number(value)
  if (Number.isNaN(n)) return '-'
  return n.toLocaleString()
}

/**
 * 充足率の表示。
 * バックエンドは座席数が 0/不明のとき null を返す仕様なので、そのまま「—」を出す。
 * 0 と「データなし」を混同させないため、ここで 0% に潰さないことが重要。
 */
export function formatRate(value) {
  if (value == null) return '—'
  return formatPercent(value)
}

/** MetricGroup（合計 + 朝/昼/夜）をそのまま安全に取り出す */
function pickMetricGroup(group) {
  const g = group || {}
  return {
    total: g.total ?? 0,
    morning: g.morning ?? 0,
    afternoon: g.afternoon ?? 0,
    night: g.night ?? 0,
  }
}

/** RateGroup（合計 + 朝/昼/夜、各々 null 可）。null は潰さず保持する */
function pickRateGroup(group) {
  const g = group || {}
  return {
    total: g.total ?? null,
    morning: g.morning ?? null,
    afternoon: g.afternoon ?? null,
    night: g.night ?? null,
  }
}

/** 劇場ランキング1行を表示用モデルへ */
export function mapCinemaRankItem(item) {
  // 主数字用点估计；下方的区间提示仍展示 min〜max 两个真实边界
  const boxFmt = formatBoxOfficeAmount(item.boxOffice ?? item.maxBoxOffice)
  const minFmt = formatBoxOfficeAmount(item.minBoxOffice)
  const maxFmt = formatBoxOfficeAmount(item.maxBoxOffice)
  return {
    rankNo: item.rankNo,
    cinemaId: item.cinemaId,
    cinemaName: item.cinemaName || '-',
    prefectureName: item.prefectureName || '',
    cinemaChainName: item.cinemaChainName || '',
    boxOffice: boxFmt.value,
    boxOfficeUnit: boxFmt.unit,
    // 推定区間であることを示すツールチップ用（下限〜上限）
    boxOfficeRangeText: `${minFmt.value}${minFmt.unit} 〜 ${maxFmt.value}${maxFmt.unit}`,
    scheduleCount: pickMetricGroup(item.scheduleCount),
    seatTotal: pickMetricGroup(item.seatTotal),
    occupiedSeats: pickMetricGroup(item.occupiedSeats),
    occupancyRate: pickRateGroup(item.occupancyRate),
  }
}

/** レスポンスから劇場配列を取り出す（想定外の形でも落ちないように） */
export function pickCinemaList(res) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object') return []
  const data = res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res
  return Array.isArray(data.cinemaList) ? data.cinemaList : []
}

/**
 * 4次元それぞれの選択肢を正規化。
 * バックエンドは各次元の**全量**（ep2_version マスタ）を返し、その作品・日付・地域で
 * 実際に上映があるかを available で示す。available:false の項目は表示したままグレーアウトする。
 * 「指定なし」は versionId=NONE で末尾に来る。
 */
export function pickVersionOptions(res) {
  const data = res && typeof res === 'object' ? res : {}
  const result = {}
  VERSION_DIMENSIONS.forEach(({ optionsKey }) => {
    const list = Array.isArray(data[optionsKey]) ? data[optionsKey] : []
    result[optionsKey] = list
      .filter((item) => item && item.versionId != null)
      .map((item) => ({
        versionId: String(item.versionId),
        name: item.name || String(item.versionId),
        // 旧レスポンス（available なし）は「返ってきた＝実在する」なので true 扱い
        available: item.available !== false,
      }))
  })
  return result
}

/** 週次詳細の日別データをグラフ用に。興行収入と入場者数をそれぞれ独立スケールで正規化する */
export function mapWeeklyDaily(dailyList) {
  const rows = Array.isArray(dailyList) ? dailyList : []
  if (!rows.length) return []

  // 2本のグラフは別々の最大値で正規化する（軸を共有すると片方が潰れて読めない）。
  // 全て 0 のときは 1 を分母にして高さ 0 の棒を出す
  const maxBox = Math.max(...rows.map((row) => Number(row.boxOffice ?? row.maxBoxOffice) || 0), 1)
  const maxOcc = Math.max(...rows.map((row) => Number(row.occupiedSeats) || 0), 1)
  const today = dayjs().startOf('day')

  return rows.map((row) => {
    const box = Number(row.boxOffice ?? row.maxBoxOffice) || 0
    const occ = Number(row.occupiedSeats) || 0
    const boxFmt = formatBoxOfficeAmount(box)
    const d = dayjs(row.date)
    return {
      date: row.date,
      dayOfWeek: row.dayOfWeek || '',
      dateLabel: d.isValid() ? d.format('M/D') : row.date,
      isToday: d.isValid() && d.startOf('day').isSame(today),
      isWeekend: row.dayOfWeek === '土' || row.dayOfWeek === '日',
      boxOffice: box,
      boxOfficeText: `${boxFmt.value}${boxFmt.unit}`,
      boxOfficeHeight: Math.round((box / maxBox) * 100),
      occupiedSeats: occ,
      occupiedSeatsText: formatCount(occ),
      occupiedSeatsHeight: Math.round((occ / maxOcc) * 100),
      scheduleCount: Number(row.scheduleCount) || 0,
      seatTotal: Number(row.seatTotal) || 0,
      occupancyRate: row.occupancyRate ?? null,
    }
  })
}

/** 週次詳細のサマリー行（週合計）を表示用に */
export function mapWeeklySummary(detail) {
  const d = detail || {}
  const boxFmt = formatBoxOfficeAmount(d.weekBoxOffice ?? d.weekMaxBoxOffice)
  return {
    boxOffice: boxFmt.value,
    boxOfficeUnit: boxFmt.unit,
    occupiedSeats: formatCount(d.weekOccupiedSeats),
    seatTotal: formatCount(d.weekSeatTotal),
    occupancyRate: formatRate(d.weekOccupancyRate),
  }
}

/** 曜日の日本語1文字。dayjs の day()（0=日曜）を添字に使う */
const DAY_OF_WEEK_JP = ['日', '月', '火', '水', '木', '金', '土']

/**
 * 公開週の期間表示：2026/09/25（金）〜 10/01（木）
 * 曜日は日付から算出する（公開週の起点曜日が変わっても表示がずれないように固定文字列にしない）
 */
export function formatWeekRange(weekStart, weekEnd) {
  const s = dayjs(weekStart)
  const e = dayjs(weekEnd)
  if (!s.isValid() || !e.isValid()) return ''
  return `${s.format('YYYY/MM/DD')}（${DAY_OF_WEEK_JP[s.day()]}）〜 ${e.format('MM/DD')}（${DAY_OF_WEEK_JP[e.day()]}）`
}

/**
 * 公開週のラベル。バックエンドの weekNo は 1 起点で、0 以下は公開前（前夜祭・先行上映）の週：
 * 1 →「第1週」、0 →「公開前1週」、-1 →「公開前2週」
 */
export function formatWeekLabel(weekNo) {
  const n = Number(weekNo)
  if (!Number.isFinite(n)) return ''
  return n >= 1 ? `第${n}週` : `公開前${1 - n}週`
}

/** 周セレクタに並べる weekNo の一覧（minWeekNo〜maxWeekNo、昇順）。min 省略時は 1 から */
export function buildWeekNoList(minWeekNo, maxWeekNo) {
  const max = Number(maxWeekNo)
  if (!Number.isFinite(max)) return []
  const minRaw = Number(minWeekNo)
  const min = Number.isFinite(minRaw) ? Math.min(minRaw, 1) : 1
  const list = []
  for (let n = min; n <= max; n += 1) list.push(n)
  return list
}
